// The signed-in user's account (password, two-factor authentication, sessions) and, for
// administrators, the firm's users, the audit log and the security settings (docs/SECURITY.md).
// Online only: these calls are never queued in the outbox.
import type {
  AdminResetHistory,
  AuditEntry,
  ChangePasswordRequest,
  CreateUserRequest,
  ManagedUser,
  MeResponse,
  ReauthRequest,
  ReauthResponse,
  RecoveryCodesResponse,
  SecurityAlerts,
  SecuritySettings,
  SessionInfo,
  TotpSetupResponse,
  UpdateUserRequest,
} from '../../../shared/types';
import { authChanged } from './api';
import { ApiClientError, bumpAuthEpoch, request } from './http';
import { notifySessionChanged } from './outbox';

const enc = encodeURIComponent;

// ---------- own account ----------

/**
 * The answer may have replaced the session cookie (a restricted session that became a full one is
 * rotated): a 401 to a request sent before, with the old cookie, is stale – here and in the other
 * tabs, which share the cookie.
 */
function sessionMayHaveRotated(): void {
  bumpAuthEpoch();
  notifySessionChanged();
}

export function getMe(): Promise<MeResponse> {
  return request<MeResponse>('/api/auth/me');
}

/** POST /api/auth/password. Other sessions of the user end; the features are re-read afterwards. */
export async function changePassword(req: ChangePasswordRequest): Promise<void> {
  await request<void>('/api/auth/password', { method: 'POST', body: req });
  sessionMayHaveRotated();
  authChanged();
}

/** POST /api/auth/totp/setup: a new (not yet active) secret with its QR code. */
export function totpSetup(): Promise<TotpSetupResponse> {
  return request<TotpSetupResponse>('/api/auth/totp/setup', { method: 'POST' });
}

/**
 * POST /api/auth/totp/enable. Resolves the recovery codes (shown once). The features are NOT
 * re-read here: the caller shows the codes first and calls api.authChanged() afterwards.
 */
export async function totpEnable(code: string): Promise<string[]> {
  const res = await request<RecoveryCodesResponse>('/api/auth/totp/enable', {
    method: 'POST',
    body: { code: code.replace(/\s+/g, '') },
  });
  sessionMayHaveRotated();
  return res.recoveryCodes;
}

export interface ConfirmIdentity {
  password: string;
  /** 6-digit code from the authenticator app, or a recovery code. */
  code: string;
}

function confirmBody(req: ConfirmIdentity): ConfirmIdentity {
  return { password: req.password, code: req.code.trim().replace(/\s+/g, '') };
}

export async function totpDisable(req: ConfirmIdentity): Promise<void> {
  await request<void>('/api/auth/totp/disable', { method: 'POST', body: confirmBody(req) });
  authChanged();
}

/** New recovery codes; the old ones stop working. */
export async function regenerateRecoveryCodes(req: ConfirmIdentity): Promise<string[]> {
  const res = await request<RecoveryCodesResponse>('/api/auth/recovery-codes', { method: 'POST', body: confirmBody(req) });
  return res.recoveryCodes;
}

/**
 * POST /api/auth/reauth: password (and code) again, so this session may do administrator actions
 * for 10 minutes. Throws ApiClientError 401 `invalid_password`, 400 `invalid_code` /
 * `code_required`, 429 `too_many_attempts`.
 */
export function reauthenticate(req: ReauthRequest): Promise<ReauthResponse> {
  const code = req.code?.trim().replace(/\s+/g, '');
  return request<ReauthResponse>('/api/auth/reauth', { method: 'POST', body: code ? { password: req.password, code } : { password: req.password } });
}

/** An administrator action was refused until the user re-authenticates (see ReauthDialog). */
export function isReauthRequired(err: unknown): boolean {
  return err instanceof ApiClientError && err.status === 403 && err.code === 'reauth_required';
}

/** Resets of the own password / 2FA by an administrator (last 90 days), with the sign-ins after each. */
export function getAdminResets(): Promise<AdminResetHistory> {
  return request<AdminResetHistory>('/api/auth/admin-resets');
}

/** The notice about the resets up to `upTo` was read (the history stays). */
export async function markAdminResetsSeen(upTo: number): Promise<void> {
  await request<void>('/api/auth/admin-resets/seen', { method: 'POST', body: { upTo } });
}

// ---------- own sessions ----------

export function listSessions(): Promise<SessionInfo[]> {
  return request<SessionInfo[]>('/api/auth/sessions');
}

export async function revokeSession(id: string): Promise<void> {
  await request<void>(`/api/auth/sessions/${enc(id)}`, { method: 'DELETE' });
}

export async function revokeOtherSessions(): Promise<void> {
  await request<void>('/api/auth/sessions/revoke-others', { method: 'POST' });
}

// ---------- administration ----------

export function listUsers(): Promise<ManagedUser[]> {
  return request<ManagedUser[]>('/api/users');
}

export function createUser(req: CreateUserRequest): Promise<ManagedUser> {
  return request<ManagedUser>('/api/users', { method: 'POST', body: { ...req, username: req.username.trim(), displayName: req.displayName.trim() } });
}

export function updateUser(id: string, req: UpdateUserRequest): Promise<ManagedUser> {
  return request<ManagedUser>(`/api/users/${enc(id)}`, { method: 'PATCH', body: req });
}

export async function deleteUser(id: string): Promise<void> {
  await request<void>(`/api/users/${enc(id)}`, { method: 'DELETE' });
}

export function listUserSessions(id: string): Promise<SessionInfo[]> {
  return request<SessionInfo[]>(`/api/users/${enc(id)}/sessions`);
}

/** Newest first; `before`: the id of the oldest entry shown so far (to load older ones). */
export function listAudit(opts: { limit?: number; before?: number } = {}): Promise<AuditEntry[]> {
  const qs = new URLSearchParams();
  if (opts.limit) qs.set('limit', String(opts.limit));
  if (opts.before) qs.set('before', String(opts.before));
  const query = qs.toString();
  return request<AuditEntry[]>(`/api/audit${query ? `?${query}` : ''}`);
}

export function getSecuritySettings(): Promise<SecuritySettings> {
  return request<SecuritySettings>('/api/security-settings');
}

/** Signs of password guessing (many failed sign-ins from many addresses) in the last 30 days. */
export function getSecurityAlerts(): Promise<SecurityAlerts> {
  return request<SecurityAlerts>('/api/security-alerts');
}

/** Saving may restrict this very session (2FA required, none set up): the features are re-read. */
export async function saveSecuritySettings(settings: SecuritySettings): Promise<SecuritySettings> {
  const saved = await request<SecuritySettings>('/api/security-settings', { method: 'PUT', body: settings });
  authChanged();
  return saved;
}

// ---------- Android app ----------

export const ANDROID_APK_PATH = '/android/baam-transkripcija.apk';
/** The Wear OS watch app (docs/WEAR.md): installed on the watch only, never on the phone. */
export const WEAR_APK_PATH = '/android/baam-transkripcija-ura.apk';

/** One published app in web/public/android/version.json. */
export interface PublishedApp {
  versionCode: number;
  versionName: string;
  sha256?: string;
}

/**
 * web/public/android/version.json, written by the Android build (docs/ANDROID.md): the phone app's
 * fields at the top level (its update check reads them), the watch app under "wear".
 */
export interface AndroidVersion extends PublishedApp {
  url?: string;
  /** Absent until a watch app is published. */
  wear?: PublishedApp;
}

/** A version entry as published, or null when it is not one (no version name). */
function publishedApp(v: unknown): PublishedApp | null {
  if (!v || typeof v !== 'object') return null;
  const o = v as { versionCode?: unknown; versionName?: unknown; sha256?: unknown };
  if (typeof o.versionName !== 'string' || !o.versionName) return null;
  return {
    versionCode: typeof o.versionCode === 'number' ? o.versionCode : 0,
    versionName: o.versionName,
    ...(typeof o.sha256 === 'string' && /^[0-9a-f]{64}$/i.test(o.sha256) ? { sha256: o.sha256.toLowerCase() } : {}),
  };
}

/** The published Android apps' versions, or null when none is published (404). */
export async function getAndroidVersion(): Promise<AndroidVersion | null> {
  try {
    const v = await request<{ wear?: unknown } | null>('/android/version.json');
    const phone = publishedApp(v);
    if (!phone) return null;
    const wear = publishedApp(v?.wear);
    return { ...phone, ...(wear ? { wear } : {}) };
  } catch (err) {
    // Not published yet (404), or the dev server answers with the app shell (not JSON).
    if (err instanceof ApiClientError && (err.status === 404 || err.code === 'bad_response')) return null;
    throw err;
  }
}

// ---------- temporary passwords ----------

/** No 0/O, 1/l/I: read aloud or typed from a note without mistakes. */
const PASSWORD_ALPHABET = 'abcdefghijkmnpqrstuvwxyz23456789';

/**
 * A strong random temporary password, e.g. "k7mq-x2pf-9tnw-h3rd" (16 random characters from 32,
 * 80 bits). The user has to replace it at the first login.
 */
export function generateTemporaryPassword(): string {
  const out: string[] = [];
  const max = Math.floor(256 / PASSWORD_ALPHABET.length) * PASSWORD_ALPHABET.length;
  while (out.length < 16) {
    const bytes = crypto.getRandomValues(new Uint8Array(32));
    for (const b of bytes) {
      // Rejection sampling: no bias towards the first letters.
      if (b < max && out.length < 16) out.push(PASSWORD_ALPHABET[b % PASSWORD_ALPHABET.length]!);
    }
  }
  return [0, 4, 8, 12].map((i) => out.slice(i, i + 4).join('')).join('-');
}
